import AppContext from "@/AppContext";
import SubNavigationMobile from "@/components/elements/SubNavigationMobile";
import ModalActivateSubscription from "@/components/ModalActivateSubscription";
import Spinner from "@/components/Spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import useAuth from "@/hooks/auth";
import AccountLayout from "@/layouts/AccountLayout";
import {
  applyCouponNextBilling,
  getNextBillingAmount,
  resumeUserSubscription,
  stopUserSubscription,
  storeCancellationReason,
} from "@/services/api/Stripe";
import { fromNow } from "@/utils/datetime";
import { ArrowFatLinesUp, ArrowRight, Info } from "@phosphor-icons/react";
import dayjs from "dayjs";
import Link from "next/link";
import { useRouter } from "next/router";
import { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";

export default function Plan() {
  const router = useRouter();
  const { user } = useAuth({ middleware: "auth" });
  const { state } = useContext(AppContext);
  const { planSubscription, loading, refreshUserSubscription } = state;

  const [isOpen, setIsOpen] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const [stopReason, setStopReason] = useState(false);
  const [nextBillingData, setNextBillingData] = useState(null);
  const [loadingApplyCoupon, setLoadingApplyCoupon] = useState(false);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const [otherReason, setOtherReason] = useState("");

  const handleReasonChange = (e) => {
    const value = e.target.value;
    setCancellationReason(value);
    if (value !== "Others") {
      setOtherReason("");
    }
  };

  //   For Testing
  //   planSubscription = {
  //     ...planSubscription,
  //     has_active_subscription: true,
  //     active_plan: {
  //       amount: 1290,
  //       currency: "usd",
  //       interval: "month",
  //     },
  //     active_product: {
  //       name: "Startup Registration",
  //     },
  //     subscription_detail: {
  //       status: "trialing",
  //       cancel_at_period_end: false,
  //     },
  //   };

  const handleSaveReason = async () => {
    try {
      const response = await storeCancellationReason({
        reason: cancellationReason,
        other_reason: cancellationReason === "Others" ? otherReason : null,
        subscription_id: planSubscription.subscription_detail.id,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const onStopSubscription = async () => {
    if (!planSubscription) return;
    setLoadingCancel(true);
    const response = await stopUserSubscription(
      planSubscription.subscription_detail.id
    );
    await handleSaveReason();
    setTimeout(() => {
      setLoadingCancel(false);
      location.reload();
    }, 100);
  };

  const onStopUserSubs = async () => {
    if (!nextBillingData?.discount?.coupon) {
      setConfirmStop(true);
    } else {
      onConfirmStop();
    }
  };

  const onConfirmStop = async () => {
    setStopReason(true);
  };

  const onResumeUserSubs = async () => {
    if (!planSubscription) return;
    const response = await resumeUserSubscription(
      planSubscription.subscription_detail.id
    );
    // refreshUserSubscription();
    location.reload();
  };

  const fetchNextBilling = async () => {
    const res = await getNextBillingAmount(
      planSubscription.subscription_detail?.id
    );
    setNextBillingData(res.data);
  };

  const applyCouponForNextBilling = async () => {
    setLoadingApplyCoupon(true);
    try {
      const res = await applyCouponNextBilling(
        planSubscription.subscription_detail.id
      );
      toast.success(
        "Congratulations, you are successfully granted a discount of 20% for the next payment!"
      );
      fetchNextBilling();
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingApplyCoupon(false);
    }
  };

  useEffect(() => {
    if (planSubscription) {
      fetchNextBilling();
    }
  }, [planSubscription]);

  return (
    <AccountLayout title={"My Plan | Lucidity Insights"}>
      <div className='container'>
        <SubNavigationMobile label='Subscription' />
        <p className='text-2xl font-semibold'>My Plan</p>
        <p className='pb-5 mt-1.5 text-sm border-b text-white/40'>
          Manage your current plan.
        </p>
        {loading && (
          <div className='rounded-md px-6 py-5 border-y border-x border-y-white/10 border-x-white/[0.03] bg-gradient-to-t from-white/[0.05] to-white/[0.07] shadow-md mt-5 h-36'>
            <div className='flex items-center justify-center h-full'>
              <Spinner /> Retrieving user plan
            </div>
          </div>
        )}

        {!loading && (
          <div className='rounded-md px-4 lg:px-6 py-3 lg:py-5 border-y border-x border-y-white/10 border-x-white/[0.03] bg-gradient-to-t from-white/[0.05] to-white/[0.07] shadow-md mt-5'>
            <div className='flex items-center justify-between'>
              <div className='flex flex-col space-y-0.5 lg:space-y-1.5'>
                <div className='inline-flex items-center space-x-2 leading-none'>
                  <span className='text-xs opacity-30'>Current Plan</span>
                  {planSubscription &&
                    planSubscription.has_active_subscription && (
                      <Badge className='text-[11px] font-medium px-1.5 py-0.5 capitalize'>
                        {planSubscription?.subscription_detail?.status ==
                        "trialing"
                          ? "Trial"
                          : planSubscription?.subscription_detail?.status}
                      </Badge>
                    )}
                  {planSubscription &&
                    planSubscription.has_active_subscription &&
                    planSubscription.subscription_detail.status ==
                      "trialing" && (
                      <div className='-mt-1.5'>
                        <Button
                          onClick={() => setIsOpen(true)}
                          variant='link'
                          className='h-auto p-0 text-[11px] underline'
                        >
                          Activate Full Subscription
                        </Button>
                      </div>
                    )}
                </div>
                <span className='text-lg font-bold lg:text-2xl'>
                  {planSubscription && planSubscription.has_active_subscription
                    ? planSubscription?.active_product?.name
                    : "Free Plan"}
                </span>
              </div>
              <div className='text-right space-y-0.5'>
                <p className='font-normal text-[11px] opacity-30'>
                  {planSubscription && planSubscription.has_active_subscription
                    ? `Billed ${planSubscription?.active_plan.interval[0].toUpperCase()}${planSubscription?.active_plan.interval.slice(
                        1
                      )}ly`
                    : "Free Forever"}
                </p>
                <div className='text-xl font-bold lg:text-3xl'>
                  <span className='uppercase'>
                    {planSubscription &&
                    planSubscription.has_active_subscription
                      ? planSubscription?.active_plan.currency
                      : ""}
                  </span>{" "}
                  {planSubscription && planSubscription.has_active_subscription
                    ? (
                        planSubscription?.active_plan.amount / 100
                      ).toLocaleString("en", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : 0}
                </div>
              </div>
            </div>

            {planSubscription && planSubscription.has_active_subscription ? (
              <div className='flex justify-between items-center pt-2.5 mt-4 space-x-8 border-t border-white/10'>
                <div className='flex items-center space-x-7'>
                  <div className='inline-flex items-center space-x-1.5'>
                    <p className='text-[11px] opacity-30'>Effective From :</p>
                    <p className='text-[11px] font-semibold'>
                      {dayjs(planSubscription?.current_period_start).format(
                        "DD MMMM YYYY"
                      )}
                    </p>
                  </div>
                  {planSubscription.subscription_detail.cancel_at_period_end ||
                  planSubscription.subscription_detail.status === "canceled" ? (
                    <div className='inline-flex items-center space-x-1.5'>
                      <p className='text-[11px] font-semibold text-red-500'>
                        Will be cancelled at :
                      </p>
                      <p className='text-[11px] font-semibold'>
                        {dayjs(planSubscription?.current_period_end).format(
                          "DD MMMM YYYY"
                        )}
                      </p>
                    </div>
                  ) : (
                    <div className='inline-flex items-center space-x-1.5'>
                      <p className='text-[11px] opacity-30'>Next Billing :</p>
                      <p className='text-[11px] font-semibold'>
                        {dayjs(planSubscription?.current_period_end).format(
                          "DD MMMM YYYY"
                        )}{" "}
                        for{" "}
                        {nextBillingData?.next_billing_amount?.toLocaleString()}{" "}
                        <span className='uppercase'>
                          {nextBillingData?.currency}
                        </span>{" "}
                        {nextBillingData?.discount?.coupon?.percent_off && (
                          <span>
                            ({nextBillingData.discount.coupon.percent_off}% OFF)
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
                <div className='inline-flex items-center space-x-3.5'>
                  {planSubscription?.active_product?.name !=
                    "Startup Registration" && (
                    <Button
                      onClick={() => router.push("/pricing")}
                      variant='link'
                      className='h-auto p-0 text-[11px] underline'
                    >
                      Upgrade Plan
                    </Button>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant='link'
                        className={`h-auto p-0 text-[11px] ${
                          planSubscription.subscription_detail
                            .cancel_at_period_end
                            ? "text-green-500"
                            : "text-red-500"
                        }`}
                      >
                        {planSubscription.subscription_detail
                          .cancel_at_period_end
                          ? "Resume"
                          : "Stop"}{" "}
                        {planSubscription.subscription_detail.status ==
                        "trialing"
                          ? "My Trial"
                          : "My Subscription"}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className='gap-0 p-0 bg-white text-background'>
                      <div className='p-4 pt-3 pb-0'>
                        <p className='text-lg font-semibold tracking-tight'>
                          Are you sure want to{" "}
                          {planSubscription.subscription_detail
                            .cancel_at_period_end
                            ? "resume"
                            : "stop"}{" "}
                          your{" "}
                          {planSubscription.subscription_detail.status ==
                          "trialing"
                            ? "trial"
                            : "subscription"}
                          ?
                        </p>
                      </div>
                      <div className='p-4 pb-1 pt-2 overflow-y-auto max-h-[75vh] gap-x-3 gap-y-4'>
                        <div className='text-sm text-background/70'>
                          {!planSubscription.subscription_detail
                            .cancel_at_period_end ? (
                            <p>
                              {planSubscription.subscription_detail.status !==
                              "trialing" ? (
                                <>
                                  You have an active subscription until{" "}
                                  {dayjs(
                                    planSubscription?.current_period_end
                                  ).format("DD MMMM YYYY")}
                                  . However, if you proceed, you will lose your
                                  benefits immediately.
                                  {nextBillingData?.discount?.coupon
                                    ?.percent_off &&
                                    ` Additionally, you will also lose the ${nextBillingData?.discount?.coupon?.percent_off}% OFF discount for the next billing.`}
                                  {/* , this action will only affect the next
                                  billing, so dont worry, you can still enjoy
                                  the benefit until the end of this period. */}
                                </>
                              ) : (
                                <>
                                  Your free trial to the Startup Registration
                                  package will end. This will mean that you will
                                  no longer have access to the investor and
                                  startup database, and you will not be able to
                                  edit your Startup Profile going forward.
                                </>
                              )}
                            </p>
                          ) : (
                            <p>
                              This action will enable your next billing. Your
                              next billing date is{" "}
                              {dayjs(
                                planSubscription?.current_period_end
                              ).format("DD MMMM YYYY")}
                              .
                            </p>
                          )}
                        </div>
                      </div>
                      <AlertDialogFooter className='flex px-4 py-3 lg:gap-1'>
                        <AlertDialogCancel
                          className={`text-white ${
                            planSubscription.subscription_detail
                              .cancel_at_period_end
                              ? "border text-background border-gray-300 shadow-sm hover:bg-gray-100"
                              : "bg-red-800 hover:bg-red-900 hover:text-white border-red-800"
                          }`}
                        >
                          Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                          className={`w-full text-white font-medium ${
                            planSubscription.subscription_detail
                              .cancel_at_period_end
                              ? "bg-green-800 hover:bg-green-900"
                              : "border text-background border-gray-300 shadow-sm hover:bg-gray-100"
                          }`}
                          onClick={
                            planSubscription.subscription_detail
                              .cancel_at_period_end
                              ? onResumeUserSubs
                              : onStopUserSubs
                          }
                        >
                          {planSubscription.subscription_detail
                            .cancel_at_period_end
                            ? "Resume"
                            : "Stop"}{" "}
                          {planSubscription.subscription_detail.status ==
                          "trialing"
                            ? "My Trial"
                            : "My Subscription"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  {!nextBillingData?.discount?.coupon && (
                    <AlertDialog
                      open={confirmStop}
                      onOpenChange={setConfirmStop}
                    >
                      <AlertDialogContent className='gap-0 p-0 bg-white text-background'>
                        <div className='p-4 pt-3 pb-0'>
                          <p className='text-lg font-semibold tracking-tight'>
                            Stay with us, and 20% off on your next billing!
                          </p>
                        </div>
                        <div className='p-4 pb-1 pt-2 overflow-y-auto max-h-[75vh] gap-x-3 gap-y-4'>
                          <p className='text-sm text-background/70'>
                            How about 20% off your next year subscription on us!
                            Continue now and redeem your offer
                          </p>
                        </div>
                        <AlertDialogFooter className='flex px-4 py-3 lg:gap-1'>
                          <AlertDialogCancel asChild>
                            <Button
                              onClick={onConfirmStop}
                              className='bg-white border border-gray-300 shadow-sm whitespace-nowrap text-background hover:bg-gray-100'
                            >
                              No, I want to stop
                            </Button>
                          </AlertDialogCancel>
                          <AlertDialogAction
                            disabled={loadingApplyCoupon}
                            onClick={applyCouponForNextBilling}
                            className='w-full font-medium text-white bg-background hover:bg-gray-800 hover:text-white'
                          >
                            Continue and accept offer
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  <AlertDialog open={stopReason} onOpenChange={setStopReason}>
                    <AlertDialogContent className='gap-0 p-0 bg-white text-background'>
                      <div className='p-4 pt-3 pb-0'>
                        <p className='text-lg font-semibold tracking-tight'>
                          We are sorry to see you go!
                        </p>
                      </div>
                      <div className='p-4 py-2 overflow-y-auto max-h-[75vh] gap-x-3 gap-y-4'>
                        <p className='text-sm text-background/70'>
                          What was your main reason for stopping your
                          subscription? It will help us provide better service
                          in the future.
                        </p>
                        <div>
                          <div className='gap-2.5 mt-3 flex flex-col gap-[5px]'>
                            <div className='flex items-center space-x-2 text-sm'>
                              <input
                                type='radio'
                                value='I found a better alternative'
                                id='reason-1'
                                name='cancellation_reason'
                                className='flex items-center justify-center w-4 h-4 relative after:w-2 border-black/20 after:h-2 after:rounded-[50%] data-[state="checked"]:after:bg-black'
                                onChange={handleReasonChange}
                              />
                              <label
                                className='font-normal cursor-pointer'
                                htmlFor='reason-1'
                              >
                                I found a better alternative
                              </label>
                            </div>
                            <div className='flex items-center space-x-2 text-sm'>
                              <input
                                type='radio'
                                value='Price is too high'
                                id='reason-2'
                                name='cancellation_reason'
                                className='flex items-center justify-center w-4 h-4 relative after:w-2 border-black/20 after:h-2 after:rounded-[50%] data-[state="checked"]:after:bg-black'
                                onChange={handleReasonChange}
                              />
                              <label
                                className='font-normal cursor-pointer'
                                htmlFor='reason-2'
                              >
                                Price is too high
                              </label>
                            </div>
                            <div className='flex items-center space-x-2 text-sm'>
                              <input
                                type='radio'
                                value="I don't have time to use it"
                                id='reason-3'
                                name='cancellation_reason'
                                className='flex items-center justify-center w-4 h-4 relative after:w-2 border-black/20 after:h-2 after:rounded-[50%] data-[state="checked"]:after:bg-black'
                                onChange={handleReasonChange}
                              />
                              <label
                                className='font-normal cursor-pointer'
                                htmlFor='reason-3'
                              >
                                {`I don't have time to use it`}
                              </label>
                            </div>
                            <div className='flex items-center space-x-2 text-sm'>
                              <input
                                type='radio'
                                value='Technical Issues or Bugs'
                                id='reason-4'
                                name='cancellation_reason'
                                className='flex items-center justify-center w-4 h-4 relative after:w-2 border-black/20 after:h-2 after:rounded-[50%] data-[state="checked"]:after:bg-black'
                                onChange={handleReasonChange}
                              />
                              <label
                                className='font-normal cursor-pointer'
                                htmlFor='reason-4'
                              >
                                Technical Issues or Bugs
                              </label>
                            </div>
                            <div className='flex items-center space-x-2 text-sm'>
                              <input
                                type='radio'
                                value='Dissatisfied with the service'
                                id='reason-5'
                                name='cancellation_reason'
                                className='flex items-center justify-center w-4 h-4 relative after:w-2 border-black/20 after:h-2 after:rounded-[50%] data-[state="checked"]:after:bg-black'
                                onChange={handleReasonChange}
                              />
                              <label
                                className='font-normal cursor-pointer'
                                htmlFor='reason-5'
                              >
                                Dissatisfied with the service
                              </label>
                            </div>
                            <div className='flex items-center space-x-2 text-sm'>
                              <input
                                type='radio'
                                value='Others'
                                id='reason-6'
                                name='cancellation_reason'
                                className='flex items-center justify-center w-4 h-4 relative after:w-2 border-black/20 after:h-2 after:rounded-[50%] data-[state="checked"]:after:bg-black'
                                onChange={handleReasonChange}
                              />
                              <label
                                className='font-normal cursor-pointer'
                                htmlFor='reason-6'
                              >
                                Others
                              </label>
                            </div>
                          </div>
                          {cancellationReason === "Others" && (
                            <div>
                              <Textarea
                                rows={4}
                                className='mt-3 bg-white text-background border-background/20 focus-visible:ring-0'
                                value={otherReason}
                                onChange={(e) => setOtherReason(e.target.value)}
                              />
                              <p className='mt-1.5 text-xs opacity-50'>
                                Please type your reason here
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      <AlertDialogFooter className='flex px-4 py-3 lg:gap-1'>
                        <AlertDialogCancel className='bg-white border border-gray-300 shadow-sm whitespace-nowrap text-background hover:bg-gray-100'>
                          Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={onStopSubscription}
                          className='w-full font-medium text-white bg-background hover:bg-gray-800 hover:text-white'
                          disabled={
                            (cancellationReason === "Others" && !otherReason) ||
                            !cancellationReason ||
                            loadingCancel
                          }
                        >
                          Stop My Subscription
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ) : (
              <div className='flex flex-wrap lg:flex-nowrap items-center pt-3 lg:pt-4 mt-2.5 lg:space-x-3 border-t border-white/10'>
                <Button
                  onClick={() => router.push(`/pricing`)}
                  variant='outline'
                  size='sm'
                  className='mt-3 lg:mt-0 w-full lg:w-auto bg-gradient-to-tr from-yellow-1 to-red-1 flex-shrink-0 text-[11px] px-2 py-1.5 h-auto'
                >
                  <ArrowFatLinesUp className='flex-shrink-0 mr-1.5' /> Upgrade
                  my plan
                </Button>
                <p className='order-first lg:order-last text-white/30 text-[11px]'>
                  You have limited access to our content in monthly period{" "}
                  <Link legacyBehavior href='/pricing'>
                    <a className='inline-block text-white border-b border-dashed border-white/40'>
                      See more detail
                    </a>
                  </Link>
                  . Upgrade your plan to enjoy more content from us so you can
                  stay up to date!
                </p>
              </div>
            )}
            {planSubscription &&
              planSubscription.has_active_subscription &&
              planSubscription.subscription_detail.status == "trialing" &&
              !planSubscription.subscription_detail.cancel_at_period_end && (
                <div className='flex pt-4 mt-2.5 space-x-1.5 border-t border-white/10'>
                  <Info className='flex-shrink-0 text-base' />
                  <p className='text-xs translate-y-px text-white/40'>
                    Your free trial on{" "}
                    <span className='text-white'>
                      {planSubscription?.active_product?.name} plan{" "}
                    </span>
                    is ending{" "}
                    {fromNow(
                      dayjs.unix(
                        planSubscription?.subscription_detail.trial_end
                      )
                    )}
                    .You will be charged on{" "}
                    <span className='text-white'>
                      {dayjs(planSubscription?.current_period_end).format(
                        "DD MMMM YYYY"
                      )}
                    </span>
                  </p>
                </div>
              )}
          </div>
        )}
        <div className='w-[150px] text-xs mt-5'>
          <Link legacyBehavior href='/terms-of-service#cancellation-policy'>
            <a className='inline-flex items-center font-medium'>
              <ArrowRight weight='bold' className='mr-2 text-sm underline' />
              Cancellation Policy
            </a>
          </Link>
        </div>
        <ModalActivateSubscription
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          planSubscription={planSubscription}
        />
      </div>
    </AccountLayout>
  );
}
